import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from "react-router-dom";  // Use Routes instead of Switch
import Home from "./pages/landing/Home";
import Login from "./pages/auth/Login";
import SignUp from "./pages/auth/SignUp";
import PrivacyPolicy from "./pages/legal/PrivacyPolicy";
import TermsConditions from "./pages/legal/TermsConditions";
import ForgotPasswordPage from "./pages/auth/ForgotPasswordPage";
import ResetPasswordPage from "./pages/auth/ResetPasswordPage";
import AdminLogin from "./admin/auth/AdminLogin";
import AdminLayout from "./admin/AdminLayout";
import AdminRoute from "./routes/auth/AdminRoute";
import ProtectedRoute from "./routes/auth/ProtectedRoute";
import { ThemeProvider } from "./context/ThemeContext";
import { useAuth } from "./context/AuthContext";
import { JobProvider } from "./context/JobContext";
import Header from "./components/header/Header"
import './css/dashboard.css';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { MessagingProvider } from "./context/MessagingContext";
import HomeEntry from "./routes/auth/HomeEntry";
import Logout from "./routes/auth/Logout";
import DashboardRoutes from "./routes/DashboardRoutes";
import DashboardShell from "./layouts/DashboardShell";
import AppLayout from "./layouts/AppLayout";
import Services from "./pages/services/Services";
import PublicUserRoute from "./routes/auth/PublicUserRoute";
import ServiceRequests from "./pages/service-requests/ServiceRequests";
import ServiceRequestDetailPage from "./pages/service-requests/ServiceRequestDetailPage";
import ProfileEntry from "./pages/profile/ProfileEntry";
import FreelancerProfileEntry from "./pages/freelancer-profile/FreelancerProfileEntry";
import BusinessProfilePublicView from "./pages/business-profile/public/BusinessProfilePublicView";
import { useDispatch } from "react-redux";
import { fetchProfile } from "./store/profileSlice";
import FreelancerPublicProfile from "./pages/freelancer-profile/public/FreelancerPublicProfile";
import BusinessProfileEntry from "./pages/business-profile/BusinessProfileEntry";
import FreelancersPage from "./pages/freelancers/FreelancersPage";
import BusinessesPage from "./pages/businesses/BusinessesPage";

const HeaderWrapper = ({ toggleNav }) => {
  const location = useLocation();
  const hideHeaderPaths = [
    "/admin",
    "/admin/login",
    "/privacy-policy",
    "/terms-and-conditions",
  ];
  const shouldHideHeader = hideHeaderPaths.some(path =>
    location.pathname.startsWith(path)
  );

  return <Header toggleNav={toggleNav} hidden={shouldHideHeader} />;
};

const App = () => {
  const { accessToken, role, isLoggingOut } = useAuth();
  const [isNavVisible, setIsNavVisible] = useState(() => window.innerWidth > 768);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const dispatch = useDispatch();

  const toggleNav = () => {
    if (window.innerWidth <= 768) {
      setIsNavVisible(prev => !prev);
    } else {
      setIsCollapsed(prev => !prev);
    }
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 768) setIsNavVisible(false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (accessToken && role !== "admin" && !isLoggingOut) {
      dispatch(fetchProfile());
    }
  }, [accessToken, role, isLoggingOut, dispatch]);

  return (
    <Router>
      <MessagingProvider>

        <ThemeProvider>
          <JobProvider>

            <div className="fullscreen">
              <ToastContainer position="top-right" autoClose={3000} />
              <HeaderWrapper toggleNav={toggleNav} />

              <Routes>

                <Route
                  element={
                    <PublicUserRoute
                      isNavVisible={isNavVisible}
                      isCollapsed={isCollapsed}
                      closeNav={() => setIsNavVisible(false)}
                    />
                  }
                >
                  <Route path="/freelancers" element={<FreelancersPage />} />
                  <Route path="/businesses" element={<BusinessesPage />} />
                  <Route path="/service-requests" element={<ServiceRequests />} />
                  <Route path="/service-requests/:id" element={<ServiceRequestDetailPage />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/terms-and-conditions" element={<TermsConditions />} />
                  <Route path="/profile/:id/*" element={<ProfileEntry />} />
                  <Route path="/business/:id/*" element={<BusinessProfileEntry />} />
                  <Route path="/freelancer/:id/*" element={<FreelancerProfileEntry />} />
                </Route>

                <Route element={<ProtectedRoute publicOnly />}>
                  <Route
                    element={
                      <AppLayout
                        isNavVisible={isNavVisible}
                        isCollapsed={isCollapsed}
                        closeNav={() => setIsNavVisible(false)}
                      />
                    }
                  >
                    <Route path="/" element={<Home />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/signup" element={<SignUp />} />
                    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                    <Route path="/reset-password" element={<ResetPasswordPage />} />
                    <Route path="/admin/login" element={<AdminLogin />} />
                  </Route>
                </Route>

                {/* Private users */}
                <Route element={<ProtectedRoute />}>
                  <Route
                    element={
                      <AppLayout
                        isNavVisible={isNavVisible}
                        isCollapsed={isCollapsed}
                        closeNav={() => setIsNavVisible(false)}
                      />
                    }
                  >
                    <Route element={<DashboardShell />}>
                      <Route path="/home/*" element={<DashboardRoutes />} />
                    </Route>
                  </Route>
                </Route>

                {/* Admin */}
                <Route element={<AdminRoute />}>
                  <Route path="/admin/*" element={<AdminLayout />} />
                </Route>

                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </div>
          </JobProvider>
        </ThemeProvider>
      </MessagingProvider>
    </Router >
  );
};

export default App;

